import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/i18n";
import { BarChart3, TrendingUp } from "lucide-react";
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";

const MONTH_LABELS_ES = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
const MONTH_LABELS_EN = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export default function ConversionChart() {
  const { t, lang } = useI18n();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/leads/conversion-stats?months=6")
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading || !data) return null;
  const { buckets, totals } = data;
  if (!buckets || buckets.length === 0) return null;

  const monthLabels = lang === "en" ? MONTH_LABELS_EN : MONTH_LABELS_ES;
  const chartData = buckets.map((b) => ({
    name: `${monthLabels[b.month - 1]} '${String(b.year).slice(-2)}`,
    created: b.created,
    enrolled: b.enrolled,
    rate: b.conversion_rate,
    inProgress: !!b.in_progress,
  }));
  const hasData = totals.created > 0;

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="conversion-chart">
      <div className="flex items-start justify-between mb-3 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-brand-blue/10 flex items-center justify-center">
            <BarChart3 className="h-4 w-4 text-brand-blue" />
          </div>
          <div>
            <div className="font-semibold text-sm text-slate-900">{t("conversion_chart_title")}</div>
            <div className="text-xs text-slate-500">{t("conversion_chart_subtitle")}</div>
          </div>
        </div>
        <div className="flex items-center gap-4 text-xs">
          <div>
            <div className="text-slate-400 uppercase tracking-wider">{t("conversion_chart_created")}</div>
            <div className="text-slate-900 font-semibold text-lg">{totals.created}</div>
          </div>
          <div>
            <div className="text-slate-400 uppercase tracking-wider">{t("conversion_chart_enrolled")}</div>
            <div className="text-emerald-600 font-semibold text-lg" data-testid="conversion-enrolled-total">
              {totals.enrolled}
              {typeof totals.enrolled_active_now === "number" && (
                <span className="text-slate-400 font-normal text-xs ml-1.5" data-testid="conversion-active-now">
                  · {totals.enrolled_active_now} {t("conversion_chart_active_now")}
                </span>
              )}
            </div>
          </div>
          <div>
            <div className="text-slate-400 uppercase tracking-wider flex items-center gap-1">
              <TrendingUp className="h-3 w-3" />{t("conversion_chart_rate")}
            </div>
            <div className={`font-semibold text-lg ${totals.conversion_rate >= 25 ? "text-emerald-600" : totals.conversion_rate >= 10 ? "text-amber-600" : "text-rose-600"}`}>
              {totals.conversion_rate}%
            </div>
          </div>
        </div>
      </div>

      {hasData ? (
        <div className="h-[180px]" data-testid="conversion-chart-canvas">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" tick={({ x, y, payload, index }) => (
                <text x={x} y={y + 12} textAnchor="middle" fontSize={11}
                  fill={chartData[index]?.inProgress ? "#f59e0b" : "#64748b"}
                  fontWeight={chartData[index]?.inProgress ? 600 : 400}>
                  {payload.value}{chartData[index]?.inProgress ? " •" : ""}
                </text>
              )} />
              <YAxis yAxisId="left" tick={{ fontSize: 11, fill: "#64748b" }} allowDecimals={false} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: "#64748b" }} domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
              <Tooltip
                contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid #e2e8f0" }}
                formatter={(value, name) => {
                  if (name === "rate") return [`${value}%`, t("conversion_chart_rate")];
                  if (name === "created") return [value, t("conversion_chart_created")];
                  if (name === "enrolled") return [value, t("conversion_chart_enrolled")];
                  return [value, name];
                }}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} formatter={(v) => {
                if (v === "rate") return t("conversion_chart_rate");
                if (v === "created") return t("conversion_chart_created");
                if (v === "enrolled") return t("conversion_chart_enrolled");
                return v;
              }} />
              <Bar yAxisId="left" dataKey="created" fill="#3b82f6" radius={[4, 4, 0, 0]} />
              <Bar yAxisId="left" dataKey="enrolled" fill="#10b981" radius={[4, 4, 0, 0]} />
              <Line yAxisId="right" type="monotone" dataKey="rate" stroke="#f59e0b" strokeWidth={2.5} dot={{ r: 3, fill: "#f59e0b" }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="text-xs text-slate-400 italic text-center py-6">
          {t("conversion_chart_no_data")}
        </div>
      )}
      {hasData && (
        <div className="text-[11px] text-amber-600 mt-1" data-testid="conversion-current-month-note">
          {t("conversion_chart_current_month_note")}
        </div>
      )}
    </div>
  );
}
